// cleaningGestures.js
// The browser's writes to a file under review, its units and its batch (Spec 2
// section 6.1). Each gesture is one Firestore transaction that reads what it
// needs first, checks on the live documents that the change is allowed now
// (Spec 2 section 6.2), and writes the decisions, the request or the approval
// together with every status they change, derived by utils/ingestionStatus.js.
//
// A gesture that is refused writes nothing and throws a GestureRefused, whose
// message is the plain sentence the review surface shows the person. Any other
// error a gesture meets (the SDK's permission or transaction errors, a storage
// failure, a call made with the wrong arguments) is not written for the person:
// the surface shows CHANGE_NOT_MADE in its place and logs the error
// (gestureFailureMessage).
//
// What a gesture never writes: a cleaning claim, what a run saw or when it
// ran, a unit's copies, the pool, an accepted answer (cleaning.md, "What may
// be edited, and when"). firestore.rules admits exactly the writes made here
// (fileUpdateAllowed, deletionRequestAllowed, unitDocumentWriteAllowed).
//
// The same transactions, written in Python for the live tests, are in
// functions/tests/live_tests/support/gestures.py.

import {
  FieldPath, Timestamp, arrayRemove, arrayUnion, doc, getDoc, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadString } from 'firebase/storage';
import { db, platformAuth, storage } from './firebase';
import { fetchGcsBlobByPrefix } from './api_utils';
import { PROCESS_SUMMARIES, newVersion, tableScope } from '../utils/blobPaths';
import {
  activeValue, activeVersion, deactivateDecisions, decisionListOf, everyVersion, findDecision, newRecord,
  requestRun, resetDecision, setPersonVersion, spilledListPath, versionsOf, withDecisionList, withSpilledList,
} from '../utils/decisions';
import {
  APPROVED_UNIT_EDIT_BLOCK, BUSY_FILE_EDIT_BLOCK, BUSY_UNIT_EDIT_BLOCK, COMPLETED_EDIT_BLOCK,
  RUNNING_BATCH_EDIT_BLOCK, SUBMITTING_EDIT_BLOCK, batchEditBlock, fileEditBlock, fileHasWarnings,
  fileIsBusy, fileStatusFrom, memberState, statusFrom, unitChainProcessIds, unitEditBlock,
  unitFileEditBlock,
} from '../utils/ingestionStatus';
import { isProfileLearned } from '../utils/profileLearning';
import { sheetPlanProblems } from '../utils/sheetPlans';
import { stableStringify } from '../utils/stableJson';

// A change the live documents do not allow now, or that cannot be made as it
// was asked for. Its message is a plain sentence for the person.
export class GestureRefused extends Error {}

// What the person is told when a gesture fails for any reason but a refusal.
export const CHANGE_NOT_MADE = 'Decode could not make this change. Try again.';

// The sentence to show the person for an error a gesture threw: a refusal's
// own, or CHANGE_NOT_MADE for anything else, which is logged for us to read.
export function gestureFailureMessage(error) {
  if (error instanceof GestureRefused) return error.message;
  console.error('A cleaning change failed:', error);
  return CHANGE_NOT_MADE;
}

// The dialog's sentence when a disposition change would leave nothing to ingest.
export const NO_INGEST_UNIT_LEFT = "A file can't be ingested when every unit is ignored.";

// Shown before a change to an approved file, or a file in a batch, is submitted (Spec 2 section 6.4).
export const REWIND_CONFIRMATION = 'This file is already approved. Making this change will un-approve '
  + 'the file and take it out of the check it is in and discard the answers about its rows. '
  + 'Approve it again when you are done.';

// A whole-value save (a sheet's plan, a document's table) refused because the
// decision changed after the draft was seeded: the editor keeps its draft and
// reloads the decision (Spec 2 section 6.6).
export const CHANGED_WHILE_EDITING = 'Someone else saved a change here while you were editing. '
  + 'Reload to see it, then make your change again.';

const SIGNED_OUT = 'You are signed out. Sign in again to make this change.';
const FILE_GONE = 'This file no longer exists.';
const UNIT_GONE = 'This table is no longer part of this file.';
const UNIT_OF_ANOTHER_FILE = "This file's list of tables names a table of another file. Reload the file.";
const BATCH_GONE = 'The check this file was in no longer exists. Reload the file.';
const TABLE_GONE = 'The table this file belongs to no longer exists.';
const PROFILE_GONE = "This file's data profile no longer exists.";
const STEP_NOT_IN_FILE = 'This step has not run on this file.';
const STEP_NOT_RUN_ON_UNIT = 'This step has not run on this table yet.';
const STEP_NOT_IN_CHAIN = 'This step is no longer part of cleaning: its rule or column was removed in Table Settings.';
const NOT_EDITABLE = 'Decode sets this value itself; it cannot be changed.';
const NOTHING_TO_UNDO = 'There is nothing to undo here.';
const SOURCE_CHANGED = "This table's source changed while you were editing. Review it again.";
const KEPT_CHANGING = 'This table kept changing while your change was being saved. Try again.';
const FOREIGN_DECISION_LIST = "This table's list of values is stored outside its own folder, so Decode will not read it.";
const LEFT_BATCH = 'This file is no longer part of the check it was in. Reload the file.';
const NOT_IN_THIS_BATCH = 'A chosen file is no longer part of this check. Reload the check.';
const CHECK_GONE = 'This check no longer exists. Reload the file.';
const NO_FILES_CHOSEN = 'Choose the files to take out of this check.';
const FILE_ALREADY_APPROVED = 'This file is already approved.';
const UNIT_NOT_INGESTED = 'This table is not being ingested.';
const UNIT_ALREADY_APPROVED = 'This table is already approved.';
const UNIT_NOT_APPROVED = 'This table is not approved.';
const UNIT_NOT_TO_REVIEW = 'This table is not ready to approve yet.';
const TABLES_NOT_SETTLED = 'Every table must finish cleaning and pass its checks before this file can be approved.';
const PROFILE_NOT_LEARNED = "Decode has not learned this data profile's layout yet: review and approve each table first.";
const APPROVE_EVERY_TABLE = 'Approve or ignore every table before approving this file.';
const UNKNOWN_DATE_MODE = "This table's reporting date setting is not one Decode knows. Fix it in Table Settings.";
const ALREADY_DELETING = 'This file is already being deleted.';
const NO_TABLES_TO_INGEST = 'This file has no tables set to be ingested.';
const NO_TABLES_ON_SHEET = 'This sheet has no tables to change.';
const SHEET_NOT_IN_FILE = 'This sheet is not part of this file.';
const SHEET_BUSY = 'Decode is updating a table on this sheet.';
const SHEET_NOT_IGNORED = 'This sheet is no longer fully ignored. Reload the file.';
const HEADER_VALUES_NEED_ONE_ROW = 'Only a table with a single row can be used for header values.';

// The file chain's processes (Spec 2 section 3). Pool keeps no decisions.
const FILE_PROCESSES = ['segment', 'record_sets', 'dispositions', 'date_order', 'file_name_date', 'pool'];
// The file processes whose decisions editFileDecisions and resetFileDecision
// change. Dispositions change only through setDisposition, ignoreSheet and
// unignoreSheet, which keep a file from being left with nothing to ingest and
// learn ignored layouts.
const EDITABLE_FILE_PROCESSES = ['segment', 'record_sets', 'date_order', 'file_name_date'];
// Processes whose decision is a whole structure replaced at once: a sheet's
// plan, a document's table. Their edits carry the made_at of the version the
// draft was seeded from (Spec 2 section 6.6).
const WHOLE_VALUE_PROCESSES = ['segment', 'record_sets'];
const CLASSIFY_PREFIX = 'classify:';
const DISPOSITIONS = ['ingest', 'context', 'ignore'];
const DATE_MODES = ['latest', 'earliest', 'most_common'];
const FINAL_UPLOAD_STATUSES = ['completed', 'completed_with_errors', 'failed'];

// The most a Classify decision list holds inline, and the unit document's
// limit, as functions/ingestion/blob_spill.py and claims.py set them.
const INLINE_DECISION_LIST_BYTES = 200000;
const UNIT_DOCUMENT_BYTES_LIMIT = 1000000;
// A Classify list change is prepared again at most this many times when its
// record moves between the preparation and the transaction.
const MAX_DECISION_LIST_ATTEMPTS = 3;

// Whether a change to this file must first be confirmed with
// REWIND_CONFIRMATION: the file is approved or in a batch, so the change
// un-approves it and takes it out of its batch. The same condition makes the
// gestures rewind.
export function needsRewindConfirmation(file) {
  return file.approved != null || file.batch_id != null;
}

// ── Reading and writing one gesture's documents ────────────────────────────

function signedInUid() {
  const user = platformAuth.currentUser;
  if (!user) throw new GestureRefused(SIGNED_OUT);
  return user.uid;
}

// The documents of one table that a gesture reads and writes.
function tableDocuments(companyId, tableId) {
  const scope = tableScope(companyId, tableId);
  return {
    table: doc(db, scope),
    file: (fileId) => doc(db, `${scope}/files/${fileId}`),
    unit: (unitId) => doc(db, `${scope}/units/${unitId}`),
    batch: (batchId) => doc(db, `${scope}/batches/${batchId}`),
    profile: (profileId) => doc(db, `${scope}/data_profiles/${profileId}`),
    upload: (uploadId) => doc(db, `${scope}/uploads/${uploadId}`),
  };
}

// The writes of one gesture, gathered so that each document gets one update
// in which a later value of a field replaces an earlier one: a rewind writes a
// file's units Ready, and the gesture may then write one of them PreviewReady.
// A field is named by its path segments, so a process id such as
// 'classify:No. of Units' is never split at its dot.
class GestureWrites {
  constructor() {
    this.documents = new Map();
  }

  set(documentRef, segments, value) {
    if (!this.documents.has(documentRef.path)) {
      this.documents.set(documentRef.path, { documentRef, fields: new Map() });
    }
    this.documents.get(documentRef.path).fields.set(segments.join('\u0000'), { segments, value });
  }

  commit(transaction) {
    for (const { documentRef, fields } of this.documents.values()) {
      const fieldsAndValues = [];
      for (const { segments, value } of fields.values()) {
        fieldsAndValues.push(new FieldPath(...segments), value);
      }
      transaction.update(documentRef, ...fieldsAndValues);
    }
  }
}

// `read` is transaction.get inside a transaction, or getDoc for the reads a
// Classify list change makes before its transaction.
async function readFileAndBatch(read, documents, fileId) {
  const fileSnapshot = await read(documents.file(fileId));
  if (!fileSnapshot.exists()) throw new GestureRefused(FILE_GONE);
  const file = fileSnapshot.data();
  let batch = null;
  if (file.batch_id != null) {
    const batchSnapshot = await read(documents.batch(file.batch_id));
    if (!batchSnapshot.exists()) throw new GestureRefused(BATCH_GONE);
    batch = batchSnapshot.data();
  }
  return { file, batch };
}

async function readTable(read, documents) {
  const tableSnapshot = await read(documents.table);
  if (!tableSnapshot.exists()) throw new GestureRefused(TABLE_GONE);
  return tableSnapshot.data();
}

async function readProfile(read, documents, profileId) {
  const profileSnapshot = await read(documents.profile(profileId));
  if (!profileSnapshot.exists()) throw new GestureRefused(PROFILE_GONE);
  return profileSnapshot.data();
}

// A unit the file's ledger names, read for a file-level gesture.
async function readUnitOfFile(read, documents, unitId, fileId) {
  const unitSnapshot = await read(documents.unit(unitId));
  if (!unitSnapshot.exists()) throw new GestureRefused(UNIT_GONE);
  const unit = unitSnapshot.data();
  if (unit.file_id !== fileId) throw new GestureRefused(UNIT_OF_ANOTHER_FILE);
  return unit;
}

// A unit with its file, the file's batch and the table: what a unit gesture
// judges, the table naming the unit's chain.
async function readUnitWithFile(read, documents, unitId) {
  const unitSnapshot = await read(documents.unit(unitId));
  if (!unitSnapshot.exists()) throw new GestureRefused(UNIT_GONE);
  const unit = unitSnapshot.data();
  const { file, batch } = await readFileAndBatch(read, documents, unit.file_id);
  if (file.units[unitId] === undefined) throw new GestureRefused(UNIT_GONE);
  const table = await readTable(read, documents);
  return { unit, file, batch, table };
}

// Writes the file's fields that the gesture changed in its local view: a
// process record, the approval, the date, the batch fields, the verdicts, the
// deletion request, a ledger entry. Then the file's derived status, and, with
// any ledger entry or verdict, its warnings flag (Spec 2 section 5.1: whoever
// writes a ledger entry writes the file's status and has_warnings with it).
// A field is changed when the local view holds a new object or value for it.
function writeFileState(writes, fileDocument, before, after) {
  for (const [processId, record] of Object.entries(after.processes)) {
    if (record !== before.processes[processId]) writes.set(fileDocument, ['processes', processId], record);
  }
  for (const field of ['approved', 'date', 'batch_id', 'batch_status', 'verdicts', 'deletion']) {
    if (after[field] !== before[field]) writes.set(fileDocument, [field], after[field]);
  }
  let ledgerChanged = false;
  for (const [unitId, entry] of Object.entries(after.units)) {
    if (entry === before.units[unitId]) continue;
    writes.set(fileDocument, ['units', unitId], entry);
    ledgerChanged = true;
  }
  if (ledgerChanged || after.verdicts !== before.verdicts) {
    writes.set(fileDocument, ['has_warnings'], fileHasWarnings(after));
  }
  writes.set(fileDocument, ['status'], fileStatusFrom(after));
}

// Writes a unit's changed records and approval, and its derived status, which
// it returns.
function writeUnitState(writes, unitDocument, before, after, file, table) {
  for (const [processId, record] of Object.entries(after.processes)) {
    if (record !== before.processes[processId]) writes.set(unitDocument, ['processes', processId], record);
  }
  if (after.approved !== before.approved) writes.set(unitDocument, ['approved'], after.approved);
  const status = statusFrom(after, file, table);
  writes.set(unitDocument, ['status'], status);
  return status;
}

// Writes a unit changed by a person's edit, reset or request, and its ledger
// entry with the unit's derived status and pending: a submitted change is busy
// from this commit until the unit's next run clears pending (Spec 2 section
// 6.1). Returns the file's local view with the new entry.
function writePendingUnit(writes, unitDocument, unitId, before, after, file, table) {
  const status = writeUnitState(writes, unitDocument, before, after, file, table);
  const entry = { ...file.units[unitId], status, pending: true };
  return { ...file, units: { ...file.units, [unitId]: entry } };
}

// ── Rewind ─────────────────────────────────────────────────────────────────

// The batch with the leaving files removed from `files` and `uploaded_at`, set
// waiting, and every file still in it mirroring waiting, written without
// reading them: every batch_status write carries the derived status (Spec 3
// section 1.4), and a file still in a batch reads its batch's status.
function writeBatchWithout(writes, documents, batchId, batch, leavingFileIds) {
  const remaining = batch.files.filter((member) => !leavingFileIds.includes(member));
  const uploadedAt = {};
  for (const [member, uploaded] of Object.entries(batch.uploaded_at)) {
    if (!leavingFileIds.includes(member)) uploadedAt[member] = uploaded;
  }
  const batchDocument = documents.batch(batchId);
  writes.set(batchDocument, ['files'], remaining);
  writes.set(batchDocument, ['uploaded_at'], uploadedAt);
  writes.set(batchDocument, ['status'], 'waiting');
  writes.set(batchDocument, ['waiting_since'], serverTimestamp());
  for (const member of remaining) {
    writes.set(documents.file(member), ['batch_status'], 'waiting');
    writes.set(documents.file(member), ['status'], 'waiting');
  }
}

// The local view of a file taken out of its batch, its approval revoked. Every
// ingest unit of a file in a batch is approved, which is what Ready says, so
// each reads Ready again, written without reading the unit. The questions
// about the file's rows are left for the next walk to delete (Spec 3 section 1.5).
function fileOutOfBatch(writes, documents, file) {
  const units = { ...file.units };
  for (const [unitId, entry] of Object.entries(file.units)) {
    if (entry.disposition !== 'ingest') continue;
    units[unitId] = { ...entry, status: 'Ready' };
    writes.set(documents.unit(unitId), ['status'], 'Ready');
  }
  return { ...file, units, approved: null, batch_id: null, batch_status: null };
}

// cleaning.md's rewind, inside the caller's transaction, which has read the
// batch: revoke the file's approval and, when the file is in a batch, take it
// out, the batch and the files left in it waiting. Refused while the batch is
// running or taking answers. Returns the file's local view after the rewind;
// the caller derives and writes the file's final status.
function rewind(writes, documents, fileId, file, batch) {
  const batchBlock = batchEditBlock(file, batch);
  if (batchBlock) throw new GestureRefused(batchBlock);
  if (file.batch_id == null) return { ...file, approved: null };
  if (file.batch_status === 'completed') throw new GestureRefused(COMPLETED_EDIT_BLOCK);
  if (!batch.files.includes(fileId)) throw new GestureRefused(LEFT_BATCH);
  writeBatchWithout(writes, documents, file.batch_id, batch, [fileId]);
  return fileOutOfBatch(writes, documents, file);
}

// ── A file's own decisions ─────────────────────────────────────────────────

function requireEntries(entries) {
  if (!Array.isArray(entries) || entries.length === 0) {
    throw new Error('A change needs at least one decision.');
  }
  for (const entry of entries) {
    if (entry === null || typeof entry !== 'object' || !('key' in entry) || entry.value === undefined) {
      throw new Error('Each decision changed needs its key and its value (null for none).');
    }
  }
}

// Whether two made_at values name the same moment. A version's made_at reads
// back from Firestore as a Timestamp; a draft may hold it as a Timestamp, a
// Date, or, from a spilled decision list, ISO text.
function sameMoment(first, second) {
  if (first == null || second == null) return first == null && second == null;
  const [firstTime, secondTime] = [first, second].map((value) => {
    if (value instanceof Timestamp) return value;
    if (value instanceof Date) return Timestamp.fromDate(value);
    return Timestamp.fromDate(new Date(value));
  });
  return firstTime.isEqual(secondTime);
}

// The record with one person's version per entry. Refused when a decision is
// one Decode alone sets, or when `expectedVersions` names the made_at of the
// active version a draft was seeded from and the live active version is
// another (Spec 2 section 6.6).
function withPersonVersions(record, entries, by, expectedVersions) {
  let edited = record;
  for (const entry of entries) {
    const active = activeVersion(record, entry.key);
    if (active !== null && active.editable === false) throw new GestureRefused(NOT_EDITABLE);
    const expectsVersion = expectedVersions != null && typeof entry.key === 'string'
      && Object.hasOwn(expectedVersions, entry.key);
    if (expectsVersion && !sameMoment(expectedVersions[entry.key], active === null ? null : active.made_at)) {
      throw new GestureRefused(CHANGED_WHILE_EDITING);
    }
    const refersTo = entry.refersTo === undefined ? null : entry.refersTo;
    edited = setPersonVersion(edited, entry.key, entry.value, by, { refersTo });
  }
  return edited;
}

// The record with the decision under `key` back at its first version.
function withDecisionReset(record, key) {
  if (versionsOf(record, key).length === 0) throw new GestureRefused(NOTHING_TO_UNDO);
  const active = activeVersion(record, key);
  if (active !== null && active.editable === false) throw new GestureRefused(NOT_EDITABLE);
  return resetDecision(record, key);
}

// Why an edit of a document's tables cannot be saved, or ''; the checks of
// gestures.py::_validate_record_set_edits, in its sentences. A table's value
// is its definition as Decode found it and the person's instructions (Spec 2
// section 3.3). The definition never changes: instructions describe the
// changes. An instruction's id keeps its table, its words and its creation
// time for ever, so a scope Decode cached for an id is never applied to other
// words; replacing the words is removing the instruction and adding a new id.
// Only this gesture holds an id to its identity: the backend does not check it
// again. A value read from the document keeps its value and its page.
function recordSetEditProblem(record, entries) {
  const isMap = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  const hasExactly = (value, names) => (
    Object.keys(value).length === names.length && names.every((name) => Object.hasOwn(value, name))
  );
  const sameIdentity = (first, second) => (
    first.key === second.key && first.text === second.text && sameMoment(first.createdAt, second.createdAt)
  );
  // Every instruction id the record has ever held, with the table, the words
  // and the creation time it was stored under.
  const identities = new Map();
  for (const { key, version } of everyVersion(record)) {
    if (!isMap(version.value) || !Object.hasOwn(version.value, 'corrections')) continue;
    for (const instruction of version.value.corrections) {
      const identity = { key, text: instruction.text, createdAt: instruction.created_at };
      if (!identities.has(instruction.id)) identities.set(instruction.id, identity);
      if (!sameIdentity(identities.get(instruction.id), identity)) {
        return 'The stored instruction identity is inconsistent.';
      }
    }
  }
  const editedKeys = new Set();
  for (const entry of entries) {
    const versions = typeof entry.key === 'string' ? versionsOf(record, entry.key) : [];
    if (versions.length === 0 || editedKeys.has(entry.key)) return 'Choose each existing record set or scalar once.';
    editedKeys.add(entry.key);
    if (entry.refersTo != null) return 'Record-set instructions cannot supply their own scope or source binding.';
    const value = entry.value;
    if (entry.key.startsWith('scalar:')) {
      if (!isMap(value) || !hasExactly(value, ['value', 'page'])) {
        return 'A scalar edit must retain its value and source page.';
      }
      continue;
    }
    if (!isMap(value) || !hasExactly(value, ['definition', 'corrections'])) {
      return 'A record-set edit must contain only its definition and corrections.';
    }
    // The first version is the definition as Decode found it.
    if (stableStringify(value.definition) !== stableStringify(versions[0].value.definition)) {
      return 'Keep the discovered definition and describe changes with instructions.';
    }
    if (!Array.isArray(value.corrections)) return 'Record-set corrections must be a list.';
    const instructionIds = new Set();
    for (const instruction of value.corrections) {
      if (!isMap(instruction) || !hasExactly(instruction, ['id', 'text', 'promote', 'created_at'])) {
        return 'An instruction must contain only id, text, promote and created_at.';
      }
      if (typeof instruction.id !== 'string' || instruction.id === '' || instructionIds.has(instruction.id)) {
        return 'Each instruction needs its own nonempty immutable id.';
      }
      instructionIds.add(instruction.id);
      if (typeof instruction.text !== 'string' || instruction.text.trim() === '') {
        return 'An instruction must contain text.';
      }
      if (typeof instruction.promote !== 'boolean') return 'Instruction promote must be true or false.';
      // A Timestamp or a Date names an instant, as a timezone-aware datetime does.
      if (!(instruction.created_at instanceof Timestamp) && !(instruction.created_at instanceof Date)) {
        return 'An instruction requires its original timestamp with a timezone.';
      }
      const identity = { key: entry.key, text: instruction.text, createdAt: instruction.created_at };
      if (identities.has(instruction.id) && !sameIdentity(identities.get(instruction.id), identity)) {
        return 'An instruction id keeps its original record set, text and creation time. '
          + 'Remove it and append a new id to replace its text.';
      }
      identities.set(instruction.id, identity);
    }
  }
  return '';
}

// A person edits decisions of one of the file's own processes: a sheet's plan
// or header fields (Segment), a document's table or value (RecordSets), a date
// order (DateOrder, keyed 'file' or by a unit id), the date in the file name
// (FileNameDate). `entries` is [{ key, value, refersTo }]. The edit reruns the
// file chain, so it waits while the file or any unit has work running or
// pending; on an approved file, or one in a batch, it rewinds the file first.
export async function editFileDecisions(companyId, tableId, fileId, processId, entries, { expectedVersions } = {}) {
  const by = signedInUid();
  requireEntries(entries);
  if (!EDITABLE_FILE_PROCESSES.includes(processId)) {
    throw new Error(`'${processId}' is not a file step whose decisions a person edits here.`);
  }
  if (WHOLE_VALUE_PROCESSES.includes(processId)) {
    for (const entry of entries) {
      if (expectedVersions == null || !Object.hasOwn(expectedVersions, entry.key)) {
        throw new Error(`Saving '${entry.key}' whole needs the made_at of the version the draft was seeded from.`);
      }
    }
  }
  // A sheet's plan passes the checks the workspace makes before Save, which
  // the backend's validate_person_plan repeats against the sheet itself.
  if (processId === 'segment') {
    for (const entry of entries) {
      const problems = sheetPlanProblems(entry.value);
      if (problems.length > 0) throw new GestureRefused(problems[0]);
    }
  }
  const documents = tableDocuments(companyId, tableId);
  await runTransaction(db, async (transaction) => {
    const read = (documentRef) => transaction.get(documentRef);
    const { file, batch } = await readFileAndBatch(read, documents, fileId);
    const editBlock = fileEditBlock(file, batch);
    if (editBlock) throw new GestureRefused(editBlock);
    const record = file.processes[processId];
    if (record === undefined) throw new GestureRefused(STEP_NOT_IN_FILE);
    if (processId === 'record_sets') {
      const problem = recordSetEditProblem(record, entries);
      if (problem) throw new GestureRefused(problem);
    }
    const edited = withPersonVersions(record, entries, by, expectedVersions);

    const writes = new GestureWrites();
    let after = file;
    if (needsRewindConfirmation(file)) after = rewind(writes, documents, fileId, file, batch);
    after = { ...after, processes: { ...after.processes, [processId]: edited } };
    writeFileState(writes, documents.file(fileId), file, after);
    writes.commit(transaction);
  });
}

// A person returns one of the file's decisions to its first version, the
// process's own ("Undo my changes", "Use automatic date format"). As an edit:
// it waits for running or pending work and rewinds an approved or batched file.
export async function resetFileDecision(companyId, tableId, fileId, processId, key) {
  if (!EDITABLE_FILE_PROCESSES.includes(processId)) {
    throw new Error(`'${processId}' is not a file step whose decisions a person resets here.`);
  }
  const documents = tableDocuments(companyId, tableId);
  await runTransaction(db, async (transaction) => {
    const read = (documentRef) => transaction.get(documentRef);
    const { file, batch } = await readFileAndBatch(read, documents, fileId);
    const editBlock = fileEditBlock(file, batch);
    if (editBlock) throw new GestureRefused(editBlock);
    const record = file.processes[processId];
    if (record === undefined) throw new GestureRefused(STEP_NOT_IN_FILE);
    const reset = withDecisionReset(record, key);

    const writes = new GestureWrites();
    let after = file;
    if (needsRewindConfirmation(file)) after = rewind(writes, documents, fileId, file, batch);
    after = { ...after, processes: { ...after.processes, [processId]: reset } };
    writeFileState(writes, documents.file(fileId), file, after);
    writes.commit(transaction);
  });
}

// A person asks a process to run again: 'reapply' keeps every decision that
// still refers; 'start_over' proposes afresh. On a file process the request is
// the file's own; `keys` limits Segment's start-over to those sheets ("Read
// this sheet again"). A unit process (map, rule:*, skip:*, classify:*) named
// here is requested on every ingest unit of the file ("Redo this step on every
// table"). Either way an approved or batched file is rewound. A request kind
// other than 'reapply' or 'start_over' is refused by requestRun.
export async function requestFile(companyId, tableId, fileId, processId, kind, keys = null) {
  if (keys !== null) {
    if (processId !== 'segment' || kind !== 'start_over') {
      throw new Error('Only reading chosen sheets again takes a list of sheets.');
    }
    if (!Array.isArray(keys) || keys.length === 0) throw new Error('Choose at least one sheet to read again.');
  }
  if (processId === 'pool') {
    throw new Error('Shared values are gathered again on every run; there is nothing to request.');
  }
  const documents = tableDocuments(companyId, tableId);

  // A process of the file's own chain: the request is on the file's record.
  if (FILE_PROCESSES.includes(processId)) {
    await runTransaction(db, async (transaction) => {
      const read = (documentRef) => transaction.get(documentRef);
      const { file, batch } = await readFileAndBatch(read, documents, fileId);
      const editBlock = fileEditBlock(file, batch);
      if (editBlock) throw new GestureRefused(editBlock);
      const record = file.processes[processId];
      if (record === undefined) throw new GestureRefused(STEP_NOT_IN_FILE);
      if (keys !== null && keys.some((sheet) => findDecision(record, sheet) === null)) {
        throw new GestureRefused(SHEET_NOT_IN_FILE);
      }

      const writes = new GestureWrites();
      let after = file;
      if (needsRewindConfirmation(file)) after = rewind(writes, documents, fileId, file, batch);
      after = { ...after, processes: { ...after.processes, [processId]: requestRun(record, kind, keys) } };
      writeFileState(writes, documents.file(fileId), file, after);
      writes.commit(transaction);
    });
    return;
  }

  // A unit process, asked for at file level (Spec 2 section 6.1): the same
  // request on that process's record of every ingest unit of the file, each
  // unit's entry pending, and the file rewound when approved or in a batch. A
  // unit cleaning gave up on takes the request and still reads Failed.
  const requestableOnEveryUnit = processId === 'map' || processId.startsWith('rule:')
    || processId.startsWith('skip:') || processId.startsWith(CLASSIFY_PREFIX);
  if (!requestableOnEveryUnit) {
    throw new Error(`'${processId}' cannot be requested on every table of a file.`);
  }
  const startsOver = kind === 'start_over';
  // Starting a Classify process over changes each unit's decision list, which
  // may be stored in a blob, so each list is prepared before the transaction,
  // as a unit's own Classify change is (changeUnitRecord).
  const changesDecisionLists = startsOver && processId.startsWith(CLASSIFY_PREFIX);
  for (let attempt = 0; attempt < MAX_DECISION_LIST_ATTEMPTS; attempt += 1) {
    let prepared = null;
    if (changesDecisionLists) {
      const { file, batch } = await readFileAndBatch(getDoc, documents, fileId);
      const table = await readTable(getDoc, documents);
      const editBlock = fileEditBlock(file, batch);
      if (editBlock) throw new GestureRefused(editBlock);
      if (!unitChainProcessIds(table).includes(processId)) throw new GestureRefused(STEP_NOT_IN_CHAIN);
      prepared = {};
      for (const [unitId, entry] of Object.entries(file.units)) {
        if (entry.disposition !== 'ingest') continue;
        const unit = await readUnitOfFile(getDoc, documents, unitId, fileId);
        prepared[unitId] = await prepareDecisionList(companyId, tableId, unitId, unit, processId,
          recordToChange(unit, processId, true), (record) => requestedUnitRecord(record, 'start_over'));
      }
    }
    try {
      await runTransaction(db, async (transaction) => {
        const read = (documentRef) => transaction.get(documentRef);
        const { file, batch } = await readFileAndBatch(read, documents, fileId);
        const table = await readTable(read, documents);
        const unitIds = Object.entries(file.units)
          .filter(([, entry]) => entry.disposition === 'ingest')
          .map(([unitId]) => unitId);
        const units = {};
        for (const unitId of unitIds) units[unitId] = await readUnitOfFile(read, documents, unitId, fileId);
        const editBlock = fileEditBlock(file, batch);
        if (editBlock) throw new GestureRefused(editBlock);
        if (!unitChainProcessIds(table).includes(processId)) throw new GestureRefused(STEP_NOT_IN_CHAIN);
        if (unitIds.length === 0) throw new GestureRefused(NO_TABLES_TO_INGEST);
        if (prepared !== null) {
          // The file's tables changed since the lists were prepared.
          const preparedIds = Object.keys(prepared);
          const sameUnits = preparedIds.length === unitIds.length
            && unitIds.every((unitId) => preparedIds.includes(unitId));
          if (!sameUnits) throw new DecisionListMoved();
        }

        const writes = new GestureWrites();
        let fileAfter = file;
        if (needsRewindConfirmation(file)) fileAfter = rewind(writes, documents, fileId, file, batch);
        for (const unitId of unitIds) {
          const unit = units[unitId];
          const record = prepared === null
            ? requestedUnitRecord(recordToChange(unit, processId, true), kind)
            : recordFromPrepared(unit, processId, prepared[unitId], true);
          let unitAfter = { ...unit, processes: { ...unit.processes, [processId]: record } };
          if (startsOver && processId === 'map') unitAfter = { ...unitAfter, approved: null };
          fileAfter = writePendingUnit(writes, documents.unit(unitId), unitId, unit, unitAfter, fileAfter, table);
        }
        writeFileState(writes, documents.file(fileId), file, fileAfter);
        writes.commit(transaction);
      });
      return;
    } catch (error) {
      if (!(error instanceof DecisionListMoved)) throw error;
    }
  }
  throw new GestureRefused(KEPT_CHANGING);
}

// ── A unit's decisions ─────────────────────────────────────────────────────

// The decision list a Classify record may keep in a blob when it is too large
// for the unit document (Spec 2 section 4.4, 00 C.3). A change to it is
// prepared before its transaction: the list is read, changed and, when it is
// or must be spilled, uploaded as a new version under the unit's own folder.
// The transaction publishes the change only while the record still stores
// the list it was prepared from and the classified column is still mapped
// from the same source. Otherwise the gesture reads the record again,
// re-applies the change and uploads a new version: it never publishes an
// upload made from an older list. A refused upload is left for the owner's
// deletion to sweep; the browser may not delete in that folder.
class DecisionListMoved extends Error {}

// The source the classified column is mapped from: what a class choice's
// refers_to.column must name (unit_processes.Classify.still_refers).
function classifiedSource(unit, processId) {
  const mapRecord = unit.processes.map;
  if (mapRecord === undefined) return null;
  return activeValue(mapRecord, processId.slice(CLASSIFY_PREFIX.length));
}

// How a record stores its decision list, as comparable text: the blob it
// names, or every version of the list itself.
function decisionStorageText(record) {
  const path = spilledListPath(record);
  if (path !== null) return stableStringify({ path });
  return stableStringify({ versions: everyVersion(record) });
}

// A decision list in the stored JSON form blob_spill.encoded writes: a moment
// as ISO text, whether it was read as a Firestore Timestamp or made here as a Date.
function withIsoTimes(value) {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(withIsoTimes);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([name, child]) => [name, withIsoTimes(child)]));
  }
  return value;
}

// A Classify record with its decision list in hand: the record itself when it
// holds its list, or the record with the list read from its blob. The blob's
// path sits on a browser-writable record, so it is followed only inside the
// unit's own folder. The unit view's Classify step reads a spilled list
// through this as well.
export async function recordWithListLoaded(companyId, tableId, unitId, record) {
  const path = spilledListPath(record);
  if (path === null) return record;
  const folder = `${tableScope(companyId, tableId)}/${PROCESS_SUMMARIES}/${unitId}/`;
  if (typeof path !== 'string' || !path.startsWith(folder) || path.split('/').includes('..')) {
    throw new GestureRefused(FOREIGN_DECISION_LIST);
  }
  const blob = await fetchGcsBlobByPrefix(path);
  const decisions = JSON.parse(await blob.text());
  if (!Array.isArray(decisions)) throw new GestureRefused("This table's stored list of values could not be read.");
  return withDecisionList(record, decisions);
}

// Prepares a change of a Classify record's decision list for its transaction:
// the list read, changed by `change(record, unit)`, and stored where it now
// belongs. That is inline while it fits the inline budget and the unit
// document stays within its limit, otherwise a new version of its blob,
// uploaded now. A list already spilled stays spilled; the unit's next run
// decides again.
async function prepareDecisionList(companyId, tableId, unitId, unit, processId, record, change) {
  const loaded = await recordWithListLoaded(companyId, tableId, unitId, record);
  const changed = change(loaded, unit);
  const decisions = decisionListOf(changed);
  const encoded = JSON.stringify(withIsoTimes(decisions));
  let spill = spilledListPath(record) !== null
    || new TextEncoder().encode(encoded).length > INLINE_DECISION_LIST_BYTES;
  if (!spill) {
    const unitAsWritten = { ...unit, processes: { ...unit.processes, [processId]: changed } };
    spill = new TextEncoder().encode(JSON.stringify(withIsoTimes(unitAsWritten))).length > UNIT_DOCUMENT_BYTES_LIMIT;
  }
  let spilledPath = null;
  if (spill) {
    spilledPath = newVersion(tableScope(companyId, tableId), PROCESS_SUMMARIES, unitId, 'decisions.json');
    await uploadString(ref(storage, spilledPath), encoded, 'raw', { contentType: 'application/json' });
  }
  return {
    storageAsRead: decisionStorageText(record),
    sourceAsRead: classifiedSource(unit, processId),
    changedFields: {
      edited_at: changed.edited_at,
      requested: changed.requested,
      requested_keys: changed.requested_keys === undefined ? null : changed.requested_keys,
    },
    spilledPath,
    decisions,
  };
}

// The record a gesture changes: the unit's, or, for a request, a new record of
// a process that has not run on the unit yet (a rule added to the table since).
function recordToChange(unit, processId, isRequest) {
  const record = unit.processes[processId];
  if (record !== undefined) return record;
  if (isRequest) return newRecord();
  throw new GestureRefused(STEP_NOT_RUN_ON_UNIT);
}

// The live record with a prepared Classify change applied, its other fields
// (when it ran, what it saw, its notes) as the live record holds them.
function recordFromPrepared(unit, processId, prepared, isRequest) {
  const live = recordToChange(unit, processId, isRequest);
  const listMoved = decisionStorageText(live) !== prepared.storageAsRead;
  const sourceMoved = classifiedSource(unit, processId) !== prepared.sourceAsRead;
  if (listMoved || sourceMoved) throw new DecisionListMoved();
  const record = { ...live, ...prepared.changedFields };
  if (prepared.spilledPath !== null) return withSpilledList(record, prepared.spilledPath);
  return withDecisionList(record, prepared.decisions);
}

// Whether the unit's record may change now. A request may be made of an
// approved unit: it rewinds the file, as an un-approve does (Spec 2 section
// 6.1). A record outside the unit's chain is dropped at its next run, so it
// is never changed.
function refuseUnlessUnitMayChange(unitId, processId, context, isRequest) {
  const block = unitEditBlock(unitId, context.unit, context.file, context.batch, context.table);
  if (block && !(isRequest && block === APPROVED_UNIT_EDIT_BLOCK)) throw new GestureRefused(block);
  if (!unitChainProcessIds(context.table).includes(processId)) throw new GestureRefused(STEP_NOT_IN_CHAIN);
}

function commitUnitChange(transaction, documents, unitId, processId, context, record, { isRequest, revokesApproval }) {
  const writes = new GestureWrites();
  const fileId = context.unit.file_id;
  let fileAfter = context.file;
  // An edit or a reset needs no rewind: an approved unit is not editable, and
  // un-approving it rewinds the file.
  if (isRequest && needsRewindConfirmation(context.file)) {
    fileAfter = rewind(writes, documents, fileId, context.file, context.batch);
  }
  let unitAfter = { ...context.unit, processes: { ...context.unit.processes, [processId]: record } };
  if (revokesApproval) unitAfter = { ...unitAfter, approved: null };
  fileAfter = writePendingUnit(writes, documents.unit(unitId), unitId, context.unit, unitAfter, fileAfter,
    context.table);
  writeFileState(writes, documents.file(fileId), context.file, fileAfter);
  writes.commit(transaction);
}

// One person's change to one record of a unit: an edit, a reset or a request.
// `change(record, unit)` answers the record as the change leaves it, or throws
// why it cannot be made. `changesDecisions` says whether the change touches
// the decision list, which a Classify record may keep in a blob.
async function changeUnitRecord(companyId, tableId, unitId, processId, change, {
  isRequest, changesDecisions, revokesApproval,
}) {
  const documents = tableDocuments(companyId, tableId);
  const options = { isRequest, revokesApproval };
  if (!(changesDecisions && processId.startsWith(CLASSIFY_PREFIX))) {
    await runTransaction(db, async (transaction) => {
      const read = (documentRef) => transaction.get(documentRef);
      const context = await readUnitWithFile(read, documents, unitId);
      refuseUnlessUnitMayChange(unitId, processId, context, isRequest);
      const record = change(recordToChange(context.unit, processId, isRequest), context.unit);
      commitUnitChange(transaction, documents, unitId, processId, context, record, options);
    });
    return;
  }
  for (let attempt = 0; attempt < MAX_DECISION_LIST_ATTEMPTS; attempt += 1) {
    const before = await readUnitWithFile(getDoc, documents, unitId);
    refuseUnlessUnitMayChange(unitId, processId, before, isRequest);
    const prepared = await prepareDecisionList(companyId, tableId, unitId, before.unit, processId,
      recordToChange(before.unit, processId, isRequest), change);
    try {
      await runTransaction(db, async (transaction) => {
        const read = (documentRef) => transaction.get(documentRef);
        const context = await readUnitWithFile(read, documents, unitId);
        refuseUnlessUnitMayChange(unitId, processId, context, isRequest);
        const record = recordFromPrepared(context.unit, processId, prepared, isRequest);
        commitUnitChange(transaction, documents, unitId, processId, context, record, options);
      });
      return;
    } catch (error) {
      if (!(error instanceof DecisionListMoved)) throw error;
    }
  }
  throw new GestureRefused(KEPT_CHANGING);
}

// A person edits decisions of one of a unit's processes: its columns (Map), an
// undo of a rule or a skip, a class, its reporting date. `entries` is
// [{ key, value, refersTo }]. The edit waits while the file's own chain or
// this unit has work running or pending, and is refused on an approved unit;
// a sibling's work does not stop it.
export async function editUnitDecisions(companyId, tableId, unitId, processId, entries, { expectedVersions } = {}) {
  const by = signedInUid();
  requireEntries(entries);
  if (FILE_PROCESSES.includes(processId)) {
    throw new Error(`'${processId}' is a step of the file, not of one table.`);
  }
  const change = (record, unit) => {
    // The source checks of a unit edit (Spec 2 section 6.1: "existing
    // source-version checks remain"). A class choice names, as
    // refersTo.column, the source its column is mapped from now; without it
    // Classify drops the choice at the next run. A choice about a document row
    // names, as refersTo.base, the base the person saw, which a refill
    // replaces (Spec 2 section 1.3).
    for (const entry of entries) {
      const refersTo = entry.refersTo == null ? {} : entry.refersTo;
      if (processId.startsWith(CLASSIFY_PREFIX)) {
        if (!Object.hasOwn(refersTo, 'column')) {
          throw new Error('A class choice must name the source column it was made for (refersTo: { column }).');
        }
        if (refersTo.column !== classifiedSource(unit, processId)) throw new GestureRefused(SOURCE_CHANGED);
      }
      const isDocumentRow = unit.source.record_set_id != null && entry.key !== null && typeof entry.key === 'object';
      if (isDocumentRow) {
        if (!Object.hasOwn(refersTo, 'base')) {
          throw new Error('A choice about a document row must name the base it was made on (refersTo: { base }).');
        }
        if (refersTo.base !== unit.base) throw new GestureRefused(SOURCE_CHANGED);
      }
    }
    return withPersonVersions(record, entries, by, expectedVersions);
  };
  await changeUnitRecord(companyId, tableId, unitId, processId, change,
    { isRequest: false, changesDecisions: true, revokesApproval: false });
}

// A person returns one of a unit's decisions to its first version ("Undo").
export async function resetUnitDecision(companyId, tableId, unitId, processId, key) {
  if (FILE_PROCESSES.includes(processId)) {
    throw new Error(`'${processId}' is a step of the file, not of one table.`);
  }
  const change = (record) => withDecisionReset(record, key);
  await changeUnitRecord(companyId, tableId, unitId, processId, change,
    { isRequest: false, changesDecisions: true, revokesApproval: false });
}

// A unit's record carrying the person's request. A start-over also deactivates
// every decision but a rule's definition, so the surface shows them gone at
// once; the run drops them anyway (Spec 2 section 6.1).
function requestedUnitRecord(record, kind) {
  const requested = requestRun(record, kind, null);
  if (kind !== 'start_over') return requested;
  return deactivateDecisions(requested, ['definition']);
}

// A person asks one of a unit's processes to run again ("Redo this step",
// "Start this step over"). An approved unit may be asked; its file is rewound
// when approved or in a batch. A start-over on Map also revokes the unit's
// approval, since Map proposes only for an unapproved unit. A request kind
// other than 'reapply' or 'start_over' is refused by requestRun.
export async function requestUnit(companyId, tableId, unitId, processId, kind) {
  if (FILE_PROCESSES.includes(processId)) {
    throw new Error(`'${processId}' is a step of the file, not of one table.`);
  }
  const startsOver = kind === 'start_over';
  const change = (record) => requestedUnitRecord(record, kind);
  await changeUnitRecord(companyId, tableId, unitId, processId, change,
    { isRequest: true, changesDecisions: startsOver, revokesApproval: startsOver && processId === 'map' });
}

// ── What to ingest ─────────────────────────────────────────────────────────

// Refused before any write when the proposed dispositions, unit id -> value,
// would leave the file with no unit to ingest (00 A.31).
function refuseIfNothingLeftToIngest(file, proposed) {
  const ingestLeft = Object.entries(file.units).some(([unitId, entry]) => (
    (Object.hasOwn(proposed, unitId) ? proposed[unitId] : entry.disposition) === 'ingest'
  ));
  if (!ingestLeft) throw new GestureRefused(NO_INGEST_UNIT_LEFT);
}

// A unit that leaves ingest loses its approval, so that a unit used again
// later comes back for review and is learned when it is approved again (Spec 2
// section 3.4). Its status and ledger entry follow; nothing on it is pending.
// Returns the file's local view with the new entry.
function writeRevokedApproval(writes, documents, unitId, unit, file, table) {
  const unitAfter = { ...unit, approved: null };
  const status = writeUnitState(writes, documents.unit(unitId), unit, unitAfter, file, table);
  const entry = { ...file.units[unitId], status };
  return { ...file, units: { ...file.units, [unitId]: entry } };
}

// A person sets what one unit is for: ingested, header values for the other
// sheets ('context', a single-row unit only) or ignored. A region's or a
// document table's ignore teaches the data profile to ignore its layout in
// future uploads, and an un-ignore unteaches it; a fields unit's choice
// teaches nothing.
export async function setDisposition(companyId, tableId, fileId, unitId, value) {
  const by = signedInUid();
  if (!DISPOSITIONS.includes(value)) {
    throw new Error(`A table is ingested, used for header values or ignored, not ${JSON.stringify(value)}.`);
  }
  const documents = tableDocuments(companyId, tableId);
  await runTransaction(db, async (transaction) => {
    const read = (documentRef) => transaction.get(documentRef);
    const { file, batch } = await readFileAndBatch(read, documents, fileId);
    if (file.units[unitId] === undefined) throw new GestureRefused(UNIT_GONE);
    const unit = await readUnitOfFile(read, documents, unitId, fileId);
    const editBlock = fileEditBlock(file, batch);
    if (editBlock) throw new GestureRefused(editBlock);
    if (unit.claim != null || unit.cleanup_attempts > 0) throw new GestureRefused(BUSY_UNIT_EDIT_BLOCK);
    if (value === 'context' && !unit.single_row) throw new GestureRefused(HEADER_VALUES_NEED_ONE_ROW);
    refuseIfNothingLeftToIngest(file, { [unitId]: value });
    const dispositions = file.processes.dispositions;
    if (dispositions === undefined) throw new GestureRefused(STEP_NOT_IN_FILE);

    const previous = activeValue(dispositions, unitId);
    const learnsLayout = unit.source.region_id != null || unit.source.record_set_id != null;
    let layoutChange = null;
    if (learnsLayout && value === 'ignore') layoutChange = arrayUnion(unit.signature);
    if (learnsLayout && value !== 'ignore' && previous === 'ignore') layoutChange = arrayRemove(unit.signature);
    if (layoutChange !== null) await readProfile(read, documents, file.data_profile_id);
    const revokesApproval = value !== 'ingest' && unit.approved != null;
    const table = revokesApproval ? await readTable(read, documents) : null;

    const writes = new GestureWrites();
    let after = file;
    if (needsRewindConfirmation(file)) after = rewind(writes, documents, fileId, file, batch);
    const edited = setPersonVersion(dispositions, unitId, value, by);
    after = { ...after, processes: { ...after.processes, dispositions: edited } };
    if (revokesApproval) after = writeRevokedApproval(writes, documents, unitId, unit, after, table);
    writeFileState(writes, documents.file(fileId), file, after);
    if (layoutChange !== null) {
      writes.set(documents.profile(file.data_profile_id), ['disposition_signatures', 'ignored'], layoutChange);
    }
    writes.commit(transaction);
  });
}

// Ignoring or using again every unit of one sheet, in one Dispositions edit. A
// whole-sheet choice belongs to this file alone and teaches the profile nothing.
async function changeSheetDispositions(companyId, tableId, fileId, sheet, ignore) {
  const by = signedInUid();
  const documents = tableDocuments(companyId, tableId);
  await runTransaction(db, async (transaction) => {
    const read = (documentRef) => transaction.get(documentRef);
    const { file, batch } = await readFileAndBatch(read, documents, fileId);

    // The units the sheet yields, found by their deterministic ids from the
    // sheet's active plan (Spec 2 section 1.2; file_processes.unit_id_of): the
    // hex sha1 of the file id, the sheet and the region id for each table, or,
    // for a sheet with header fields and no tables, of the file id, the sheet
    // and 'fields' for its fields unit, joined with the unit-separator
    // character so that two different triples never spell one string. Only
    // ids the ledger holds count: a table whose cut failed has no unit.
    const segment = file.processes.segment;
    const plan = segment === undefined ? null : activeValue(segment, sheet);
    let sources = [];
    if (plan !== null && plan.regions.length > 0) sources = plan.regions.map((region) => region.region_id);
    else if (plan !== null && Object.keys(plan.fields).length > 0) sources = ['fields'];
    const unitIds = [];
    for (const source of sources) {
      const bytes = new TextEncoder().encode([fileId, sheet, source].join('\u001f'));
      const digest = await crypto.subtle.digest('SHA-1', bytes);
      const unitId = [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
      if (file.units[unitId] !== undefined) unitIds.push(unitId);
    }

    const units = {};
    for (const unitId of unitIds) units[unitId] = await readUnitOfFile(read, documents, unitId, fileId);
    const editBlock = fileEditBlock(file, batch);
    if (editBlock) throw new GestureRefused(editBlock);
    if (unitIds.length === 0) throw new GestureRefused(NO_TABLES_ON_SHEET);
    if (unitIds.some((unitId) => units[unitId].claim != null || units[unitId].cleanup_attempts > 0)) {
      throw new GestureRefused(SHEET_BUSY);
    }
    const dispositions = file.processes.dispositions;
    if (dispositions === undefined) throw new GestureRefused(STEP_NOT_IN_FILE);

    const proposed = {};
    if (ignore) {
      if (unitIds.every((unitId) => file.units[unitId].disposition === 'ignore')) return;
      for (const unitId of unitIds) proposed[unitId] = 'ignore';
    } else {
      if (unitIds.some((unitId) => file.units[unitId].disposition !== 'ignore')) {
        throw new GestureRefused(SHEET_NOT_IGNORED);
      }
      for (const unitId of unitIds) {
        // What the unit was for before the sheet was ignored: the latest
        // earlier version of its disposition that was not an ignore, or what
        // Dispositions proposes when there is none, header values for a
        // sheet's fields unit and ingest otherwise.
        const versions = versionsOf(dispositions, unitId);
        const activePosition = versions.findIndex((version) => version.active);
        let before = units[unitId].source.fields ? 'context' : 'ingest';
        for (let position = activePosition - 1; position >= 0; position -= 1) {
          if (versions[position].value !== 'ignore') {
            before = versions[position].value;
            break;
          }
        }
        if (before === 'context' && !units[unitId].single_row) throw new GestureRefused(HEADER_VALUES_NEED_ONE_ROW);
        proposed[unitId] = before;
      }
    }
    refuseIfNothingLeftToIngest(file, proposed);
    const revokedUnitIds = ignore ? unitIds.filter((unitId) => units[unitId].approved != null) : [];
    const table = revokedUnitIds.length > 0 ? await readTable(read, documents) : null;

    const writes = new GestureWrites();
    let after = file;
    if (needsRewindConfirmation(file)) after = rewind(writes, documents, fileId, file, batch);
    let edited = dispositions;
    for (const unitId of unitIds) edited = setPersonVersion(edited, unitId, proposed[unitId], by);
    after = { ...after, processes: { ...after.processes, dispositions: edited } };
    for (const unitId of revokedUnitIds) {
      after = writeRevokedApproval(writes, documents, unitId, units[unitId], after, table);
    }
    writeFileState(writes, documents.file(fileId), file, after);
    writes.commit(transaction);
  });
}

// "Ignore this sheet": every unit of the sheet, its fields unit included, ignored.
export async function ignoreSheet(companyId, tableId, fileId, sheet) {
  await changeSheetDispositions(companyId, tableId, fileId, sheet, true);
}

// "Use this sheet again": every unit of a fully ignored sheet back to what it was for before.
export async function unignoreSheet(companyId, tableId, fileId, sheet) {
  await changeSheetDispositions(companyId, tableId, fileId, sheet, false);
}

// ── Approval ───────────────────────────────────────────────────────────────

// A person approves one unit, judged from its ledger entry alone (Spec 2
// section 5.3): to review, its gate passed, nothing pending, and no work of
// the file's own running or pending. The unit document is written without
// being read; the unit's trigger then freezes its mapping and learns from it.
export async function approveUnit(companyId, tableId, fileId, unitId) {
  const by = signedInUid();
  const documents = tableDocuments(companyId, tableId);
  await runTransaction(db, async (transaction) => {
    const read = (documentRef) => transaction.get(documentRef);
    const { file, batch } = await readFileAndBatch(read, documents, fileId);
    const fileBlock = unitFileEditBlock(file, batch);
    if (fileBlock) throw new GestureRefused(fileBlock);
    if (file.approved != null || file.batch_id != null) throw new GestureRefused(FILE_ALREADY_APPROVED);
    const entry = file.units[unitId];
    if (entry === undefined) throw new GestureRefused(UNIT_GONE);
    if (entry.disposition !== 'ingest') throw new GestureRefused(UNIT_NOT_INGESTED);
    if (entry.pending || entry.status === 'Cleaning') throw new GestureRefused(BUSY_UNIT_EDIT_BLOCK);
    if (entry.status === 'Ready') throw new GestureRefused(UNIT_ALREADY_APPROVED);
    if (entry.gate.length > 0) {
      throw new GestureRefused(`This table cannot be approved yet: ${entry.gate.join('; ')}`);
    }
    if (entry.status !== 'PreviewReady') throw new GestureRefused(UNIT_NOT_TO_REVIEW);

    const writes = new GestureWrites();
    writes.set(documents.unit(unitId), ['approved'], { by, at: serverTimestamp() });
    writes.set(documents.unit(unitId), ['status'], 'Ready');
    const after = { ...file, units: { ...file.units, [unitId]: { ...entry, status: 'Ready' } } };
    writeFileState(writes, documents.file(fileId), file, after);
    writes.commit(transaction);
  });
}

// A person un-approves one unit, the first step of changing an approved unit:
// the file is rewound (out of its batch, its approval revoked) and the unit
// reads PreviewReady, written without reading it. The person versions the
// approval froze stay, so a later reset still reaches the process's proposal.
export async function unapproveUnit(companyId, tableId, fileId, unitId) {
  const documents = tableDocuments(companyId, tableId);
  await runTransaction(db, async (transaction) => {
    const read = (documentRef) => transaction.get(documentRef);
    const { file, batch } = await readFileAndBatch(read, documents, fileId);
    const fileBlock = unitFileEditBlock(file, batch);
    if (fileBlock) throw new GestureRefused(fileBlock);
    const entry = file.units[unitId];
    if (entry === undefined) throw new GestureRefused(UNIT_GONE);
    if (entry.disposition !== 'ingest') throw new GestureRefused(UNIT_NOT_INGESTED);
    if (entry.pending || entry.status === 'Cleaning') throw new GestureRefused(BUSY_UNIT_EDIT_BLOCK);
    if (!['Ready', 'AwaitingValidation'].includes(entry.status)) throw new GestureRefused(UNIT_NOT_APPROVED);

    const writes = new GestureWrites();
    let after = file;
    if (needsRewindConfirmation(file)) after = rewind(writes, documents, fileId, file, batch);
    writes.set(documents.unit(unitId), ['approved'], null);
    writes.set(documents.unit(unitId), ['status'], 'PreviewReady');
    after = { ...after, units: { ...after.units, [unitId]: { ...after.units[unitId], status: 'PreviewReady' } } };
    writeFileState(writes, documents.file(fileId), file, after);
    writes.commit(transaction);
  });
}

// A person approves the file. Under a learned profile (one with a learned
// mapping value and a landed file) it approves first, as approveUnit does,
// every unit still to review whose gate passed; an unlearned profile needs
// each unit approved by hand. The file must then await approval, every unit
// Ready or Ignored, and it is written approved with its reporting date and
// Ready, for the batch claim to take.
export async function approveFile(companyId, tableId, fileId) {
  const by = signedInUid();
  const documents = tableDocuments(companyId, tableId);
  await runTransaction(db, async (transaction) => {
    const read = (documentRef) => transaction.get(documentRef);
    const { file, batch } = await readFileAndBatch(read, documents, fileId);
    const profile = await readProfile(read, documents, file.data_profile_id);
    const table = await readTable(read, documents);
    const editBlock = fileEditBlock(file, batch);
    if (editBlock) throw new GestureRefused(editBlock);
    if (file.approved != null || file.batch_id != null) throw new GestureRefused(FILE_ALREADY_APPROVED);
    const learned = isProfileLearned(profile);
    const mode = table.reporting_date_mode || 'latest';
    if (!DATE_MODES.includes(mode)) throw new GestureRefused(UNKNOWN_DATE_MODE);

    const ingestEntries = Object.entries(file.units).filter(([, entry]) => entry.disposition === 'ingest');
    if (ingestEntries.some(([, entry]) => entry.pending || entry.gate.length > 0)) {
      throw new GestureRefused(TABLES_NOT_SETTLED);
    }
    const toReview = ingestEntries.filter(([, entry]) => entry.status === 'PreviewReady');
    if (toReview.length > 0 && !learned) throw new GestureRefused(PROFILE_NOT_LEARNED);

    const writes = new GestureWrites();
    const units = { ...file.units };
    for (const [unitId, entry] of toReview) {
      units[unitId] = { ...entry, status: 'Ready' };
      writes.set(documents.unit(unitId), ['approved'], { by, at: serverTimestamp() });
      writes.set(documents.unit(unitId), ['status'], 'Ready');
    }
    const unitsApproved = { ...file, units };
    if (fileStatusFrom(unitsApproved) !== 'AwaitingApproval') throw new GestureRefused(APPROVE_EVERY_TABLE);

    // The file's reporting date: the table's mode over its Ready units'
    // dates, the latest, the earliest, or the most common with a tie going to
    // the latest (Spec 2 section 5.3). The dates are ISO text, which sorts as
    // dates do.
    const dates = Object.values(units)
      .filter((entry) => entry.status === 'Ready' && entry.date != null)
      .map((entry) => entry.date)
      .sort();
    let date = null;
    if (dates.length > 0) {
      if (mode === 'latest') {
        date = dates[dates.length - 1];
      } else if (mode === 'earliest') {
        date = dates[0];
      } else {
        const counts = new Map();
        for (const candidate of dates) counts.set(candidate, (counts.get(candidate) || 0) + 1);
        // In ascending order, so a later date with as many units wins the tie.
        for (const candidate of dates) {
          if (date === null || counts.get(candidate) >= counts.get(date)) date = candidate;
        }
      }
    }

    const after = { ...unitsApproved, approved: { by, at: serverTimestamp() }, date };
    writeFileState(writes, documents.file(fileId), file, after);
    writes.commit(transaction);
  });
}

// A person submits the file once the walk's failures are addressed: every
// verdict the walk raised is removed and the file's status recomputed. Allowed
// once nothing on the file is cleaning, so every fix has landed; whether
// enough was fixed is the next walk's judgement.
export async function submitFile(companyId, tableId, fileId) {
  const documents = tableDocuments(companyId, tableId);
  await runTransaction(db, async (transaction) => {
    const read = (documentRef) => transaction.get(documentRef);
    const { file, batch } = await readFileAndBatch(read, documents, fileId);
    const editBlock = fileEditBlock(file, batch);
    if (editBlock) throw new GestureRefused(editBlock);
    const verdicts = file.verdicts.filter((verdict) => verdict.process !== 'walk');
    // Submitted already, by another person: there is nothing left to write.
    if (verdicts.length === file.verdicts.length) return;

    const writes = new GestureWrites();
    writeFileState(writes, documents.file(fileId), file, { ...file, verdicts });
    writes.commit(transaction);
  });
}

// ── Deletion and leaving a check ───────────────────────────────────────────

// A person asks for the file to be deleted (Spec 5 section 1). Refused while
// anything on the file is cleaning, while its batch is running or taking
// answers, and while a request stands; a failed deletion may be asked again.
// A file in a batch that has not landed is rewound in the same transaction,
// so a parked batch's other files wait at once. The rules admit the request
// only with requested_at the server's time and `by` the person's uid.
export async function deleteFile(companyId, tableId, fileId) {
  const by = signedInUid();
  const documents = tableDocuments(companyId, tableId);
  await runTransaction(db, async (transaction) => {
    const read = (documentRef) => transaction.get(documentRef);
    const { file, batch } = await readFileAndBatch(read, documents, fileId);
    if (fileIsBusy(file, { includeUnits: true })) throw new GestureRefused(BUSY_FILE_EDIT_BLOCK);
    const batchBlock = batchEditBlock(file, batch);
    if (batchBlock) throw new GestureRefused(batchBlock);
    if (file.deletion != null && file.deletion.failed == null) throw new GestureRefused(ALREADY_DELETING);

    const writes = new GestureWrites();
    let after = file;
    if (file.batch_id != null && file.batch_status !== 'completed') {
      after = rewind(writes, documents, fileId, file, batch);
    }
    after = {
      ...after,
      deletion: {
        requested_at: serverTimestamp(), by, claimed_at: null, failed: null, attempts: 0, transient_exits: 0,
      },
    };
    // A deletion request holds the upload open (Spec 6 section 4): a member
    // that read error, a refused file or a failed deletion, reads pending
    // again while its upload boundary is not final.
    const member = memberState(after);
    if (member !== memberState(file) && file.upload_id) {
      const uploadDocument = documents.upload(file.upload_id);
      const uploadSnapshot = await read(uploadDocument);
      const upload = uploadSnapshot.exists() ? uploadSnapshot.data() : null;
      const listsThisFile = upload !== null && upload.raw_manifest != null
        && Object.hasOwn(upload.raw_manifest, fileId);
      if (listsThisFile && !FINAL_UPLOAD_STATUSES.includes(upload.status) && upload.raw_manifest[fileId] !== member) {
        writes.set(uploadDocument, ['raw_manifest', fileId], member);
      }
    }
    writeFileState(writes, documents.file(fileId), file, after);
    writes.commit(transaction);
  });
}

// "Take files out of this check" (Spec 3 section 7.3, 00 C.16): the chosen
// files leave a parked or waiting batch together, in one transaction, so none
// is left listed on the batch and the dispatcher cannot take the batch again
// between them. Each chosen file is rewound; the files left in the batch, and
// the batch, wait.
export async function rewindFiles(companyId, tableId, batchId, fileIds) {
  if (!Array.isArray(fileIds) || fileIds.length === 0) throw new GestureRefused(NO_FILES_CHOSEN);
  if (new Set(fileIds).size !== fileIds.length) throw new Error('Choose each file once.');
  const documents = tableDocuments(companyId, tableId);
  await runTransaction(db, async (transaction) => {
    const read = (documentRef) => transaction.get(documentRef);
    const batchSnapshot = await read(documents.batch(batchId));
    if (!batchSnapshot.exists()) throw new GestureRefused(CHECK_GONE);
    const batch = batchSnapshot.data();
    const files = {};
    for (const fileId of fileIds) {
      const fileSnapshot = await read(documents.file(fileId));
      if (!fileSnapshot.exists()) throw new GestureRefused(FILE_GONE);
      files[fileId] = fileSnapshot.data();
    }
    if (batch.status === 'completed') throw new GestureRefused(COMPLETED_EDIT_BLOCK);
    if (!['parked', 'waiting'].includes(batch.status)) throw new GestureRefused(RUNNING_BATCH_EDIT_BLOCK);
    if (batch.submitting != null) throw new GestureRefused(SUBMITTING_EDIT_BLOCK);
    for (const fileId of fileIds) {
      if (!batch.files.includes(fileId) || files[fileId].batch_id !== batchId) {
        throw new GestureRefused(NOT_IN_THIS_BATCH);
      }
      const editBlock = fileEditBlock(files[fileId], batch);
      if (editBlock) throw new GestureRefused(editBlock);
    }

    const writes = new GestureWrites();
    writeBatchWithout(writes, documents, batchId, batch, fileIds);
    for (const fileId of fileIds) {
      const after = fileOutOfBatch(writes, documents, files[fileId]);
      writeFileState(writes, documents.file(fileId), files[fileId], after);
    }
    writes.commit(transaction);
  });
}
